import { Component, Prop, h, Watch, State, Element } from '@stencil/core';
import state from '@ugc-store';
import { Close } from '../../icons';
import { isRtl } from '@ugc-utils/utils';

@Component({
  tag: 'ugc-modal-dialog',
  styleUrl: 'ugc-modal-dialog.scss',
  shadow: false,
})
export class UgcModalDialog {
  @Prop() isShow: boolean = false;
  @Prop() modalTitle: string = '';
  @Prop() zIndex: number = 1;
  @Prop() isShowCloseButton: boolean = true;
  @Prop() rootStyle: any = {};
  @Prop() handleCancel: any = () => {};
  @Prop() width: number;
  @Prop() dialogTitleStyle = {};

  @State() active: boolean = false;
  @State() animateClass: string = '';

  @Element() hostElement: HTMLElement;

  timer: number;
  previouslyFocusedElement: HTMLElement = null;
  modalContentRef: HTMLDivElement;
  firstFocusableElement: HTMLElement;
  lastFocusableElement: HTMLElement;

  @Watch('isShow')
  watchShowValue(newValue) {
    if (newValue) {
      this.disableScroll();
      this.active = true;
      this.animateClass = 'enter';
      // Save the previously focused element
      this.previouslyFocusedElement = document.activeElement as HTMLElement;
      setTimeout(() => {
        this.animateClass = 'enter-active';
        // Set up focus management after the modal is visible
        this.setupFocusManagement();
      });
    } else {
      this.animateClass = 'exit';
      setTimeout(() => {
        this.animateClass = 'exit-active';
      });
    }
  }

  disableScroll = () => {
    try {
      if (this.isShow) {
        const htmlEl = document.querySelector('html');
        if (htmlEl) {
          htmlEl.style.overflow = 'hidden';
        }
      }
    } catch (e) {
      console.error('Error in disableScroll:', e);
    }
  };

  enableScroll = () => {
    try {
      if (this.timer) {
        window.clearTimeout(this.timer);
      }

      this.timer = window.setTimeout(() => {
        const drawerEle = document.querySelectorAll('[role="drawer"]');
        const dialogEle = document.querySelectorAll('[role="dialog"]:not(.modal-dialog-wrap):not(#image-viewer)');

        if (drawerEle.length > 0 || dialogEle.length > 0) {
          return;
        }

        const htmlEl = document.querySelector('html');
        if (htmlEl && htmlEl.style.overflow === 'hidden') {
          htmlEl.style.overflow = 'auto';
        }
      }, 300);
    } catch (e) {
      console.error('Error in enableScroll:', e);
    }
  };

  onTransitionEnd = () => {
    this.animateClass = this.isShow ? `enter-done` : 'exit-done';

    if (!this.isShow) {
      this.active = false;
      // Restore focus to the previously focused element when modal closes
      this.restoreFocus();

      this.enableScroll();
    }
  };

  componentDidLoad() {
    // Add keyboard event listener for trapping focus
    document.addEventListener('keydown', this.handleKeyDown);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.handleKeyDown);

    if (this.timer) {
      window.clearTimeout(this.timer);
      this.timer = null;
    }
  }

  // Find all focusable elements in the modal
  setupFocusManagement() {
    if (!this.isShow) return;

    setTimeout(() => {
      // Query all focusable elements
      const focusableElements = this.hostElement.querySelectorAll(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );

      if (focusableElements.length === 0) {
        // If no focusable elements, focus on the modal container itself
        this.modalContentRef.setAttribute('tabindex', '-1');
        this.modalContentRef.focus();
        return;
      }

      this.firstFocusableElement = focusableElements[0] as HTMLElement;
      this.lastFocusableElement = focusableElements[focusableElements.length - 1] as HTMLElement;

      // Focus on the first element
      this.firstFocusableElement.focus();
    }, 100);
  }

  restoreFocus() {
    // Return focus to the element that had focus before the modal was opened
    if (this.previouslyFocusedElement && typeof this.previouslyFocusedElement.focus === 'function') {
      this.previouslyFocusedElement.focus();
    }
  }

  handleKeyDown = (event: KeyboardEvent) => {
    if (!this.isShow) return;

    // Close the modal on Escape key
    if (event.key === 'Escape') {
      this.handleCancel();
      return;
    }

    // Handle Tab key to trap focus within modal
    if (event.key === 'Tab') {
      // If no focusable elements, do nothing
      if (!this.firstFocusableElement || !this.lastFocusableElement) return;

      // If shift + tab on first element, move to last element
      if (event.shiftKey && document.activeElement === this.firstFocusableElement) {
        event.preventDefault();
        this.lastFocusableElement.focus();
      }
      // If tab on last element, move to first element
      else if (!event.shiftKey && document.activeElement === this.lastFocusableElement) {
        event.preventDefault();
        this.firstFocusableElement.focus();
      }
    }
  };

  render() {
    if (!this.isShow && !this.active) {
      return null;
    }

    // style
    const modalWrapStyle: any = {};
    const backDropStyle: any = {};
    const modalContainerStyle: any = {};

    if (this.isShow) {
      modalWrapStyle.visibility = 'visible';
      modalWrapStyle.zIndex = `${this.zIndex}`;
    }

    if (this.zIndex !== 1) {
      backDropStyle.zIndex = `${this.zIndex}`;
      modalContainerStyle.zIndex = `${this.zIndex + 1}`;
    }
    if (this.width) {
      this.rootStyle.width = `${this.width}px`;
    }

    const closeButtonPosition = isRtl() ? { left: '16px' } : { right: '16px' };
    const modalId = `modal-dialog-${Math.random().toString(36).substr(2, 9)}`;
    const modalTitleId = `${modalId}-title`;

    return (
      <div
        class="modal-dialog-wrap flex-row-center-center"
        style={modalWrapStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby={this.modalTitle ? modalTitleId : undefined}
        onTransitionEnd={this.onTransitionEnd}
      >
        <div
          class={{
            'modal-dialog-back-drop ': true,
            [this.animateClass]: true,
          }}
          style={backDropStyle}
          onClick={this.handleCancel.bind(this)}
        />
        <div
          class={{
            'modal-dialog-desktop-container': state.isDesktop,
            'modal-dialog-mobile-container': !state.isDesktop,
            [this.animateClass]: true,
          }}
          style={modalContainerStyle}
        >
          <div
            class="modal-dialog-root"
            style={this.rootStyle}
            ref={el => (this.modalContentRef = el as HTMLDivElement)}
          >
            <div class={`flex-row-between-center ${(this.modalTitle || this.isShowCloseButton) && 'py-16 px-32'}`}>
              {this.modalTitle && (
                <div id={modalTitleId} class="fs-20 bold c-333" style={this.dialogTitleStyle}>
                  {this.modalTitle}
                </div>
              )}
              {this.isShowCloseButton && (
                <div
                  class="flex-row-end-center pointer"
                  style={closeButtonPosition}
                  onClick={this.handleCancel.bind(this)}
                  tabindex="0"
                  role="button"
                  aria-label="Close"
                  onKeyPress={e => e.key === 'Enter' && this.handleCancel()}
                >
                  <Close width="24" height="24" />
                </div>
              )}
            </div>
            <div class="modal-dialog-content">
              <slot />
            </div>
          </div>
        </div>
      </div>
    );
  }
}
